import { useEffect, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ivrNodes } from '@/api/ivrNodes';
import type { IvrNodeType, AuthMode, IvrNodeRow, OnCompleteMode } from '@/api/ivrNodes';
import { IVR_NODE_TYPES, AUTH_MODES } from '@/api/ivrNodes';
import { ivrEdges } from '@/api/ivrEdges';
import { extensions } from '@/api/extensions';
import { useAuth } from '@/lib/auth-store';
import { useCustomerContext } from '@/lib/customer-context-store';
import { MVP_TYPES, NodeTypeBadge } from './NodeTypeBadge';
import { RecordingPicker } from './RecordingPicker';
import { SystemPromptPicker } from './SystemPromptPicker';
import { EdgesList } from './EdgesList';
import { NodePromptOverridesPanel } from './NodePromptOverridesPanel';
import { AuthListPicker } from './AuthListPicker';
import { TimeFramePicker } from './TimeFramePicker';
import { TypedConfigForm, TYPED_NODE_TYPES, validateConfigForType } from './NodeConfigForms';
import { isMenuLike } from './list/AddChildPopover';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectGroup, SelectItem, SelectLabel,
  SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/cn';
import { formatRef } from '@/lib/formatRef';
import { X, Trash2, Zap, Clock, Link2, CornerDownLeft, PhoneOff, ArrowRight, Pencil } from 'lucide-react';

// ── Supported vs not-yet ──────────────────────────────────────────────────────

// "Supported" = has a typed form (includes all MVP types + everything else
// wired via TYPED_NODE_TYPES in NodeConfigForms.tsx). "Coming soon" =
// whatever is still raw JSON.
const SUPPORTED_TYPES = IVR_NODE_TYPES.filter((t) => TYPED_NODE_TYPES.has(t));
const UNSUPPORTED_TYPES = IVR_NODE_TYPES.filter((t) => !TYPED_NODE_TYPES.has(t));

// ── Section heading ───────────────────────────────────────────────────────────

function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-2 pb-1 border-b border-slate-100 mb-3">
      <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">{children}</p>
    </div>
  );
}

// ── Field wrapper ─────────────────────────────────────────────────────────────

function Field({
  label, htmlFor, required, hint, error, children,
}: {
  label: string;
  htmlFor?: string;
  required?: boolean;
  hint?: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1">
      <Label htmlFor={htmlFor} className="text-xs font-medium text-slate-600">
        {label}
        {required && <span className="ml-0.5 text-red-500" aria-hidden>*</span>}
      </Label>
      {children}
      {hint && <p className="text-[11px] text-slate-400">{hint}</p>}
      {error && <p role="alert" className="text-[11px] text-red-500">{error}</p>}
    </div>
  );
}

// ── Per-type config forms ─────────────────────────────────────────────────────

function PlaybackConfig() {
  return (
    <div className="rounded-md bg-blue-50/60 border border-blue-100 px-3 py-2.5 text-xs text-blue-700">
      Plays the main recording, then follows the &ldquo;next&rdquo; edge. No extra configuration needed.
    </div>
  );
}

function MenuConfig({
  config,
  onChange,
}: {
  config: Record<string, unknown>;
  onChange: (c: Record<string, unknown>) => void;
}) {
  const dtmf = (config.dtmf_timeout_ms as number) ?? 4000;
  const inter = (config.inter_digit_timeout_ms as number) ?? 0;
  const allowExtDial = Boolean(config.allow_extension_dialing);

  // Resolve the effective customer ID so the extensions check is scoped
  // correctly for super_admin (who operate under an entered customer context).
  const contextCustomerId = useCustomerContext((s) => s.current?.id ?? null);
  const tenantCustomerId = useAuth((s) => s.tenant?.customerId ?? null);
  const effectiveCustomerId = contextCustomerId ?? tenantCustomerId;

  // Check whether this customer has any active extensions — only show the
  // "Allow extension dialling" toggle if they do, to avoid confusing tree
  // authors who haven't set up the Extensions module.
  const { data: extCheck } = useQuery({
    queryKey: ['extensionsExist', effectiveCustomerId],
    queryFn: () =>
      extensions.list({ limit: 1, ...(effectiveCustomerId ? { customer_id: effectiveCustomerId } : {}) }),
    staleTime: 60_000,
    enabled: true,
  });
  const hasExtensions = (extCheck?.rows?.length ?? 0) > 0;

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <Field label="DTMF timeout (ms)" htmlFor="cfg-dtmf-timeout">
          <Input
            id="cfg-dtmf-timeout"
            type="number"
            min={500}
            max={60000}
            step={500}
            value={dtmf}
            onChange={(e) => onChange({ ...config, dtmf_timeout_ms: Number(e.target.value) })}
            className="h-8 text-sm font-mono"
          />
        </Field>
        <Field label="Inter-digit timeout (ms)" htmlFor="cfg-inter-digit">
          <Input
            id="cfg-inter-digit"
            type="number"
            min={0}
            step={100}
            value={inter}
            onChange={(e) => onChange({ ...config, inter_digit_timeout_ms: Number(e.target.value) })}
            className="h-8 text-sm font-mono"
          />
        </Field>
      </div>

      {/* Max digits is intentionally removed — the backend computes it
          automatically from the edge keys and extension numbers. */}

      {hasExtensions && (
        <div className="flex items-start gap-3 rounded-md border border-slate-200 bg-slate-50/60 px-3 py-2.5">
          <Switch
            id="cfg-allow-ext-dial"
            checked={allowExtDial}
            onCheckedChange={(v) => onChange({ ...config, allow_extension_dialing: v })}
            className="mt-0.5 shrink-0"
          />
          <div>
            <Label htmlFor="cfg-allow-ext-dial" className="text-xs font-medium text-slate-700 cursor-pointer">
              Allow extension dialling
            </Label>
            <p className="text-[11px] text-slate-400 mt-0.5 leading-snug">
              Callers can dial extension numbers (e.g. 101) directly from this menu.
              Max digits adjusts automatically to fit extension length.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

function TransferConfig({
  config,
  onChange,
  error,
}: {
  config: Record<string, unknown>;
  onChange: (c: Record<string, unknown>) => void;
  error?: string;
}) {
  const dest = (config.destination_e164 as string) ?? '';
  const carrierId = (config.carrier_id as string) ?? '';

  return (
    <div className="space-y-3">
      <Field
        label="Destination (E.164)"
        htmlFor="cfg-e164"
        required
        hint="e.g. +15551234567"
        error={error}
      >
        <Input
          id="cfg-e164"
          value={dest}
          onChange={(e) => onChange({ ...config, destination_e164: e.target.value })}
          placeholder="+15551234567"
          className="h-8 font-mono text-sm"
          aria-label="Destination E.164"
        />
      </Field>
      <Field
        label="Carrier ID (optional)"
        htmlFor="cfg-carrier"
        hint="Leave blank to auto-select best available carrier"
      >
        <Input
          id="cfg-carrier"
          value={carrierId}
          onChange={(e) => onChange({ ...config, carrier_id: e.target.value })}
          placeholder="auto"
          className="h-8 font-mono text-sm"
        />
      </Field>
    </div>
  );
}

function JumpConfig({
  config,
  onChange,
  treeNodes,
  currentNodeId,
}: {
  config: Record<string, unknown>;
  onChange: (c: Record<string, unknown>) => void;
  treeNodes: IvrNodeRow[];
  currentNodeId?: string;
}) {
  const targetId = (config.target_node_id as string) ?? '';
  const options = treeNodes.filter((n) => n.id !== currentNodeId);

  return (
    <Field label="Target node" htmlFor="cfg-jump-target" required hint="Cannot jump to self">
      <Select value={targetId} onValueChange={(v) => onChange({ ...config, target_node_id: v })}>
        <SelectTrigger id="cfg-jump-target" className="h-8 text-sm">
          <SelectValue placeholder="Pick target node…" />
        </SelectTrigger>
        <SelectContent>
          {options.map((n) => (
            <SelectItem key={n.id} value={n.id}>
              <span className="flex items-center gap-2">
                <span className="font-mono text-[11px] text-slate-500">{formatRef(n.ref_number)}</span>
                <span>{n.name}</span>
                <NodeTypeBadge type={n.node_type} />
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </Field>
  );
}


function GenericConfig({
  config,
  onChange,
}: {
  config: Record<string, unknown>;
  onChange: (c: Record<string, unknown>) => void;
}) {
  const [text, setText] = useState(() => JSON.stringify(config, null, 2));
  const [jsonError, setJsonError] = useState('');

  function handleBlur() {
    try {
      onChange(JSON.parse(text));
      setJsonError('');
    } catch {
      setJsonError('Invalid JSON — fix before saving.');
    }
  }

  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-1.5 text-xs text-slate-500">
        <Clock className="h-3.5 w-3.5" />
        Config schema for this node type is coming in a later phase. Free-form JSON accepted for now.
      </div>
      <Textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        onBlur={handleBlur}
        className="font-mono text-xs"
        rows={6}
        aria-label="Node config JSON"
      />
      {jsonError && <p role="alert" className="text-xs text-red-500">{jsonError}</p>}
    </div>
  );
}

// ── Auth mode select ──────────────────────────────────────────────────────────

const AUTH_LABELS: Record<AuthMode, string> = {
  open: 'Open (no check)',
  caller_id: 'Caller ID',
  pin: 'PIN',
  caller_id_and_pin: 'Caller ID + PIN',
  caller_id_or_pin: 'Caller ID or PIN',
};

// ── Delete confirm ────────────────────────────────────────────────────────────

function DeleteConfirm({ onConfirm, onCancel, isPending }: {
  onConfirm: () => void;
  onCancel: () => void;
  isPending: boolean;
}) {
  return (
    <div className="rounded-md border border-red-200 bg-red-50 p-3 space-y-2">
      <p className="text-sm font-medium text-red-800">Delete this node?</p>
      <p className="text-xs text-red-600">All edges from this node will also be removed. This cannot be undone.</p>
      <div className="flex gap-2">
        <Button variant="destructive" size="sm" onClick={onConfirm} disabled={isPending}>
          {isPending ? 'Deleting…' : 'Yes, delete'}
        </Button>
        <Button variant="outline" size="sm" onClick={onCancel} disabled={isPending}>Cancel</Button>
      </div>
    </div>
  );
}

// ── When-complete section ────────────────────────────────────────────────────
//
// UI for `ivr_nodes.on_complete` (0034) + `on_complete_node_id` (0035).
// Three choices rendered as big click-tiles (shadcn Button doesn't have a
// native RadioGroup primitive here and the tile visual matches the
// AddChildPopover mode-picker the user is already used to):
//   - Hunt back to parent — the default, safe for most playbacks
//   - Hang up             — deliberate end-of-flow
//   - Hunt to specific node — reveals a node picker
//
// The picker filters out the current node (can't hunt to self) and uses
// the drawer's existing `otherNodes` list, so it automatically respects
// the tree-scope: the drawer is always mounted against a specific tree,
// and the parent always passes in the tree's nodes.

function WhenCompleteTile({
  active, disabled, icon, title, subtitle, onClick,
}: {
  active: boolean;
  disabled?: boolean;
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={active}
      className={cn(
        'group w-full text-left rounded-md border px-3 py-2 transition-colors',
        'focus:outline-none focus:ring-2 focus:ring-indigo-400/40',
        disabled
          ? 'border-slate-100 bg-slate-50/40 opacity-50 cursor-not-allowed'
          : active
            ? 'border-indigo-300 bg-indigo-50/60 ring-1 ring-indigo-200'
            : 'border-slate-200 bg-white hover:bg-indigo-50/40 hover:border-indigo-200',
      )}
    >
      <div className="flex items-center gap-2">
        <span className={cn('shrink-0', active ? 'text-indigo-600' : 'text-slate-500')}>
          {icon}
        </span>
        <span className="text-[13px] font-medium text-slate-800 flex-1 truncate">
          {title}
        </span>
      </div>
      <p className="mt-0.5 text-[11px] text-slate-500 leading-snug pl-6">{subtitle}</p>
    </button>
  );
}

function WhenCompleteSection({
  mode, targetId, treeNodes, error,
  onModeChange, onTargetChange,
}: {
  mode: OnCompleteMode;
  targetId: string | null;
  treeNodes: IvrNodeRow[];
  error?: string;
  onModeChange: (m: OnCompleteMode) => void;
  onTargetChange: (id: string | null) => void;
}) {
  return (
    <div className="space-y-2">
      <WhenCompleteTile
        active={mode === 'hunt_back'}
        icon={<CornerDownLeft className="h-3.5 w-3.5" />}
        title="Hunt back to parent"
        subtitle="Default. Pops the call back to the menu that routed here."
        onClick={() => onModeChange('hunt_back')}
      />
      <WhenCompleteTile
        active={mode === 'hangup'}
        icon={<PhoneOff className="h-3.5 w-3.5" />}
        title="Hang up"
        subtitle="Politely ends the call after this node finishes."
        onClick={() => onModeChange('hangup')}
      />
      <WhenCompleteTile
        active={mode === 'custom_node'}
        icon={<ArrowRight className="h-3.5 w-3.5" />}
        title="Hunt to specific node"
        subtitle="Advance to the node picked below instead of popping back."
        onClick={() => onModeChange('custom_node')}
      />

      {mode === 'custom_node' && (
        <div className="pl-3 pt-1.5">
          <Field
            label="Target node"
            htmlFor="node-on-complete-target"
            error={error}
            hint="Only nodes in the current tree are listed."
          >
            <Select
              value={targetId ?? '__none__'}
              onValueChange={(v) => onTargetChange(v === '__none__' ? null : v)}
            >
              <SelectTrigger id="node-on-complete-target" className="h-9 text-sm">
                <SelectValue placeholder="Pick a target node…" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">
                  <span className="italic text-slate-400 text-sm">None</span>
                </SelectItem>
                {treeNodes.map((n) => (
                  <SelectItem key={n.id} value={n.id}>
                    <span className="flex items-center gap-1.5">
                      <span className="font-mono text-[11px] text-slate-400 shrink-0">{formatRef(n.ref_number)}</span>
                      <span className="truncate max-w-[200px] text-sm">{n.name}</span>
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
        </div>
      )}
    </div>
  );
}

// One-line description of WHEN this node type plays its Outro recording.
// Mirrors the engine wiring in backend/src/freeswitch/engine/nodes/*.ts — keep
// the two in sync. Types not listed here don't play an outro at all.
const OUTRO_BEHAVIOR_BY_TYPE: Record<string, string> = {
  playback: 'Plays as the closing tail after the recording finishes.',
  menu: 'Plays only if the caller makes no choice (timeout) — skipped when they press a key.',
  voicemail: 'Plays after the caller’s message is saved.',
  listen_voicemail: 'Plays after the caller finishes listening.',
  payment: 'Plays after a successful charge (not on a decline).',
  bulletin: 'Plays after the last message.',
  series_folder: 'Plays when the caller leaves the series.',
  sequential_folder: 'Plays after the last item.',
  unlock_series_folder: 'Plays when the caller leaves the series.',
  unlock_sequential_folder: 'Plays after the last unlocked item.',
  rotation_folder: 'Plays after the item for this call.',
  random_folder: 'Plays after the random item.',
  latest_n_folder: 'Plays after the latest item.',
  survey: 'Plays after the survey is submitted.',
  quiz: 'Plays after the score is announced.',
  conference: 'Plays as the caller leaves the room.',
  dial: 'Plays when the dial attempt ends and the caller is back on the line.',
  ring_group: 'Plays when the ring attempt ends and the caller is back on the line.',
  record: 'Plays after the recording is saved.',
  data_collection: 'Plays after the information is collected.',
  points: 'Plays after points are awarded.',
  shopping: 'Plays when checkout begins.',
  enrollment: 'Plays after the caller is enrolled.',
  public_forum: 'Plays when the caller leaves the forum.',
};

function outroBehaviorText(nodeType: string): string {
  return OUTRO_BEHAVIOR_BY_TYPE[nodeType]
    ?? 'This node type doesn’t play an outro recording.';
}

// ── Parent node section (edit mode) ──────────────────────────────────────────

function ParentNodeSection({
  nodeId,
  treeId,
  treeNodes,
  canMutate,
}: {
  nodeId: string;
  treeId: string;
  treeNodes: IvrNodeRow[];
  canMutate: boolean;
}) {
  const qc = useQueryClient();
  const [showChangeParent, setShowChangeParent] = useState(false);
  const [newParentId, setNewParentId] = useState('__none__');
  const [branchKey, setBranchKey] = useState('next');
  const [parentError, setParentError] = useState('');
  // Sam 2026-05-26: dropdown defaults to menu-like types only. "Show all"
  // toggle reveals the rest (playback chains, voicemail→saved, etc.).
  const [showAllTypes, setShowAllTypes] = useState(false);
  const branchKeyInputRef = useRef<HTMLInputElement | null>(null);

  // Fetch all tree edges to find current parent
  const { data: edgesData } = useQuery({
    queryKey: ['ivrEdgesForTree', treeId],
    queryFn: () => ivrEdges.listForTree(treeId),
    staleTime: 5000,
  });

  const incomingEdge = (edgesData?.rows ?? []).find((e) => e.to_node_id === nodeId);
  const currentParentId = incomingEdge?.from_node_id ?? null;
  const currentParent = treeNodes.find((n) => n.id === currentParentId) ?? null;

  const reparent = useMutation({
    mutationFn: () =>
      ivrNodes.reparent(nodeId, {
        parent_node_id: newParentId === '__none__' ? null : newParentId,
        branch_key: branchKey.trim() || 'next',
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ivrEdgesForTree', treeId] });
      qc.invalidateQueries({ queryKey: ['ivrNodes', treeId] });
      setShowChangeParent(false);
      setParentError('');
    },
    onError: (e: unknown) => {
      const err = e as { code?: string };
      if (err?.code === 'nodes_different_trees') {
        setParentError('Cannot assign a parent from a different tree.');
      } else if (err?.code === 'self_parent') {
        setParentError('A node cannot be its own parent.');
      } else if (err?.code === 'edge_branch_key_conflict') {
        setParentError('That branch key is already used on the selected parent.');
      } else {
        setParentError('Failed to reparent node. Please try again.');
      }
    },
  });

  function openChangeParent(focus: 'parent' | 'key' = 'parent') {
    setNewParentId(currentParentId ?? '__none__');
    setBranchKey(incomingEdge?.branch_key ?? 'next');
    setParentError('');
    setShowChangeParent(true);
    if (focus === 'key') {
      // Defer to after render so the input exists.
      setTimeout(() => branchKeyInputRef.current?.focus(), 0);
    }
  }

  // Eligible-parent filter — by default ONLY menu-like types (menu,
  // playback_settings) because picking a leaf-ish parent (playback,
  // transfer, etc.) is almost never what the user wants. When
  // showAllTypes is true, all nodes except self are eligible.
  const eligibleParents = treeNodes.filter((n) => {
    if (n.id === nodeId) return false;
    if (showAllTypes) return true;
    return isMenuLike(n.node_type);
  });
  const hiddenCount = treeNodes.filter((n) => n.id !== nodeId && !isMenuLike(n.node_type)).length;

  return (
    <div>
      <SectionHeading>Parent node</SectionHeading>

      {!showChangeParent ? (
        <div className="space-y-2 rounded-md border border-slate-100 bg-slate-50/60 px-3 py-2.5">
          {/* Parent row — name + ref + node-type badge, with its own Change btn */}
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <span className="text-[10px] font-medium uppercase tracking-wider text-slate-500 shrink-0 w-[5.5rem]">
                Parent:
              </span>
              {currentParent ? (
                <>
                  <span className="font-mono text-[10px] text-slate-400 shrink-0">
                    {formatRef(currentParent.ref_number)}
                  </span>
                  <span className="text-sm text-slate-700 truncate">{currentParent.name}</span>
                </>
              ) : (
                <span className="text-sm text-slate-400 italic">None (root or orphan)</span>
              )}
            </div>
            {canMutate && (
              <button
                type="button"
                onClick={() => openChangeParent('parent')}
                className="flex items-center gap-1 shrink-0 rounded px-2 py-1 text-xs text-indigo-600 hover:bg-indigo-50 transition-colors"
              >
                <Link2 className="h-3 w-3" />
                Change
              </button>
            )}
          </div>

          {/* Branch key row — always visible when a parent exists, with its
              own Edit button so the affordance is obvious. Sam 2026-05-26. */}
          {currentParent && incomingEdge && (
            <div className="flex items-center justify-between gap-2 border-t border-slate-100 pt-2">
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <span className="text-[10px] font-medium uppercase tracking-wider text-slate-500 shrink-0 w-[5.5rem]">
                  Branch key:
                </span>
                <span className="inline-flex items-center rounded-md border border-indigo-200 bg-indigo-50 px-2.5 py-0.5 font-mono text-sm font-semibold text-indigo-700 shrink-0">
                  {incomingEdge.branch_key}
                </span>
                <span className="text-[11px] text-slate-400 truncate">
                  (which branch from the parent leads here)
                </span>
              </div>
              {canMutate && (
                <button
                  type="button"
                  onClick={() => openChangeParent('key')}
                  className="flex items-center gap-1 shrink-0 rounded px-2 py-1 text-xs text-indigo-600 hover:bg-indigo-50 transition-colors"
                >
                  <Pencil className="h-3 w-3" />
                  Edit key
                </button>
              )}
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-3 rounded-md border border-slate-200 bg-slate-50/60 px-3 py-3">
          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-slate-600">New parent</Label>
            <Select value={newParentId} onValueChange={setNewParentId}>
              <SelectTrigger className="h-8 text-sm bg-white">
                <SelectValue placeholder="None (root / orphan)" />
              </SelectTrigger>
              <SelectContent className="max-h-56">
                <SelectItem value="__none__">
                  <span className="italic text-slate-400 text-sm">None — root / orphan</span>
                </SelectItem>
                {eligibleParents.map((n) => (
                  <SelectItem key={n.id} value={n.id}>
                    <span className="flex items-center gap-1.5">
                      <span className="font-mono text-[10px] text-slate-400 shrink-0">{formatRef(n.ref_number)}</span>
                      <span className="truncate max-w-[180px] text-sm">{n.name}</span>
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {!showAllTypes && hiddenCount > 0 && (
              <button
                type="button"
                onClick={() => setShowAllTypes(true)}
                className="text-[11px] text-indigo-600 hover:underline"
              >
                Show all node types ({hiddenCount} more) — playbacks, voicemails, etc.
              </button>
            )}
            {showAllTypes && (
              <button
                type="button"
                onClick={() => setShowAllTypes(false)}
                className="text-[11px] text-slate-500 hover:underline"
              >
                Show only menu-type parents
              </button>
            )}
          </div>

          {/* Branch key — ALWAYS visible (was previously hidden until
              parent != none, which made it look like the key wasn't
              editable). Disabled when no parent selected since a
              standalone branch key without a parent is meaningless. */}
          <div className="space-y-1.5">
            <Label htmlFor="ned-bk" className="text-xs font-medium text-slate-600">Branch key</Label>
            <Input
              ref={branchKeyInputRef}
              id="ned-bk"
              value={branchKey}
              onChange={(e) => setBranchKey(e.target.value)}
              placeholder="next, 1, 2, timeout…"
              className="h-8 text-sm font-mono bg-white"
              maxLength={32}
              disabled={newParentId === '__none__'}
            />
            <p className="text-[11px] text-slate-400">
              {newParentId === '__none__'
                ? 'Pick a parent first — a branch key is only meaningful with a parent.'
                : 'Which key on the parent menu (or "next" for linear flow) leads to this node.'}
            </p>
          </div>

          {parentError && (
            <p role="alert" className="text-xs text-red-500">{parentError}</p>
          )}

          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="flex-1 h-7 text-xs"
              onClick={() => setShowChangeParent(false)}
              disabled={reparent.isPending}
            >
              Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              className="flex-1 h-7 text-xs"
              onClick={() => reparent.mutate()}
              disabled={reparent.isPending}
            >
              {reparent.isPending ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

interface Props {
  mode: 'create' | 'edit';
  treeId: string;
  nodeId?: string;           // edit mode only
  treeNodes: IvrNodeRow[];   // all nodes in this tree (for jump / fallback dropdowns)
  canMutate: boolean;
  onClose: () => void;
  onSaved: () => void;
  onDeleted?: () => void;
}

export function NodeEditorDrawer({
  mode,
  treeId,
  nodeId,
  treeNodes,
  canMutate,
  onClose,
  onSaved,
  onDeleted,
}: Props) {
  const qc = useQueryClient();

  // Super-admin sees the "Advanced: per-node prompt overrides" section.
  // Reseller / office-manager edit the same node but don't see this — node-
  // level overrides are platform-level and only super-admin can write them.
  const authUser = useAuth((s) => s.user);
  const authTenant = useAuth((s) => s.tenant);
  const isSuperAdmin = Boolean(authUser?.is_super_admin || authTenant?.isSuperAdmin);

  // Pre-load existing node (edit mode)
  const { data: existingNode, isLoading: loadingNode } = useQuery({
    queryKey: ['ivrNode', nodeId],
    queryFn: () => ivrNodes.get(nodeId!),
    enabled: mode === 'edit' && !!nodeId,
  });

  // ── Form state ──────────────────────────────────────────────────────────────
  const [name, setName] = useState('');
  const [nodeType, setNodeType] = useState<IvrNodeType>('playback');
  const [authMode, setAuthMode] = useState<AuthMode>('open');
  const [active, setActive] = useState(true);
  // Max reprompts is no longer user-configurable — engine pins menu
  // nodes to 3 and ignores the column for everything else. Kept as a
  // constant so the create payload still satisfies the backend's
  // optional zod field. (Sam 2026-05-19)
  const MAX_REPROMPT_FIXED = 3;
  const [notes, setNotes] = useState('');

  const [mainRecId, setMainRecId] = useState<string | null>(null);
  const [intro1RecId, setIntro1RecId] = useState<string | null>(null);
  const [intro1PlayMode, setIntro1PlayMode] = useState<string>('once');
  const [intro1Skippable, setIntro1Skippable] = useState<boolean>(true);
  const [intro2RecId, setIntro2RecId] = useState<string | null>(null);
  const [intro2PlayMode, setIntro2PlayMode] = useState<string>('once');
  const [intro2Skippable, setIntro2Skippable] = useState<boolean>(true);
  const [outroRecId, setOutroRecId] = useState<string | null>(null);
  const [outroSkippable, setOutroSkippable] = useState<boolean>(true);
  const [fallbackNodeId, setFallbackNodeId] = useState<string | null>(null);

  // Audio mode: 'recording' or 'prompt' per slot
  const [mainAudioMode, setMainAudioMode] = useState<'recording' | 'prompt'>('recording');
  const [mainPromptKey, setMainPromptKey] = useState<string | null>(null);
  const [intro1AudioMode, setIntro1AudioMode] = useState<'recording' | 'prompt'>('recording');
  const [intro1PromptKey, setIntro1PromptKey] = useState<string | null>(null);
  const [intro2AudioMode, setIntro2AudioMode] = useState<'recording' | 'prompt'>('recording');
  const [intro2PromptKey, setIntro2PromptKey] = useState<string | null>(null);
  const [outroAudioMode, setOutroAudioMode] = useState<'recording' | 'prompt'>('recording');
  const [outroPromptKey, setOutroPromptKey] = useState<string | null>(null);

  const [config, setConfig] = useState<Record<string, unknown>>({});
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [serverError, setServerError] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // "When complete" — where this node routes after it finishes (see
  // migrations 0034 + 0035). Only relevant for non-menu nodes; menus
  // branch via DTMF edges instead.
  const [onComplete, setOnComplete] = useState<OnCompleteMode>('hunt_back');
  const [onCompleteNodeId, setOnCompleteNodeId] = useState<string | null>(null);

  // Auth-list gate (0036). Separate from auth_mode — this is a simple
  // blacklist/allowlist check that runs before the node handler.
  const [authListIds, setAuthListIds] = useState<string[]>([]);

  // Optional time-frame gate (migration 0076 + 0078). Universal — any
  // node type can use it. Four top-level columns: the frame ref + the
  // off-hours action + (conditional) target node + optional recording.
  const [timeFrameId, setTimeFrameId] = useState<string | null>(null);
  const [timeFrameAction, setTimeFrameAction] = useState<'hunt_back' | 'hangup' | 'route_to_node' | null>(null);
  const [timeFrameTargetNodeId, setTimeFrameTargetNodeId] = useState<string | null>(null);
  const [timeFrameRecordingId, setTimeFrameRecordingId] = useState<string | null>(null);
  // The whole gate sits behind a "Restrict by time frame" switch (Sam
  // 2026-08-06). Off is the default for nodes with no frame attached.
  // Toggling off keeps the picked fields in local state (an accidental
  // flip is undoable before saving), but a save while off sends null for
  // all four columns — detaching the frame.
  const [timeFrameGateOn, setTimeFrameGateOn] = useState(false);

  // Populate form when existing node loads
  useEffect(() => {
    if (!existingNode) return;
    setName(existingNode.name);
    setNodeType(existingNode.node_type);
    setAuthMode(existingNode.auth_mode);
    setActive(existingNode.active);
    // existingNode.max_reprompt intentionally unread — value is fixed
    setNotes(existingNode.notes ?? '');
    setMainRecId(existingNode.main_recording_id);
    setIntro1RecId(existingNode.intro1_recording_id);
    setIntro1PlayMode(existingNode.intro1_play_mode ?? 'once');
    setIntro1Skippable(existingNode.intro1_skippable ?? true);
    setIntro2RecId(existingNode.intro2_recording_id);
    setIntro2PlayMode(existingNode.intro2_play_mode ?? 'once');
    setIntro2Skippable(existingNode.intro2_skippable ?? true);
    setOutroRecId(existingNode.outro_recording_id);
    setOutroSkippable(existingNode.outro_skippable ?? true);
    setFallbackNodeId(existingNode.fallback_node_id);
    setOnComplete((existingNode.on_complete ?? 'hunt_back') as OnCompleteMode);
    setOnCompleteNodeId(existingNode.on_complete_node_id ?? null);
    setAuthListIds((existingNode as { auth_list_ids?: string[] }).auth_list_ids ?? []);
    setTimeFrameId((existingNode as { time_frame_id?: string | null }).time_frame_id ?? null);
    setTimeFrameAction((existingNode as { time_frame_action?: 'hunt_back' | 'hangup' | 'route_to_node' | null }).time_frame_action ?? null);
    setTimeFrameTargetNodeId((existingNode as { time_frame_target_node_id?: string | null }).time_frame_target_node_id ?? null);
    setTimeFrameRecordingId((existingNode as { time_frame_recording_id?: string | null }).time_frame_recording_id ?? null);
    setTimeFrameGateOn(Boolean((existingNode as { time_frame_id?: string | null }).time_frame_id));
    const cfg = (existingNode.config as Record<string, unknown>) ?? {};
    setConfig(cfg);
    // Restore audio mode from config
    if (cfg.main_prompt_key) { setMainAudioMode('prompt'); setMainPromptKey(cfg.main_prompt_key as string); }
    if (cfg.intro1_prompt_key) { setIntro1AudioMode('prompt'); setIntro1PromptKey(cfg.intro1_prompt_key as string); }
    if (cfg.intro2_prompt_key) { setIntro2AudioMode('prompt'); setIntro2PromptKey(cfg.intro2_prompt_key as string); }
    if (cfg.outro_prompt_key) { setOutroAudioMode('prompt'); setOutroPromptKey(cfg.outro_prompt_key as string); }
  }, [existingNode]);

  // ── Mutations ───────────────────────────────────────────────────────────────

  const createNode = useMutation({
    mutationFn: () => ivrNodes.create(treeId, {
      node_type: nodeType,
      name: name.trim(),
      auth_mode: authMode,
      notes: notes.trim() || undefined,
      main_recording_id: mainRecId || undefined,
      intro1_recording_id: intro1RecId || undefined,
      intro2_recording_id: intro2RecId || undefined,
      outro_recording_id: outroRecId || undefined,
      config: {
        ...config,
        ...(mainPromptKey ? { main_prompt_key: mainPromptKey } : {}),
        ...(intro1PromptKey ? { intro1_prompt_key: intro1PromptKey } : {}),
        ...(intro2PromptKey ? { intro2_prompt_key: intro2PromptKey } : {}),
        ...(outroPromptKey ? { outro_prompt_key: outroPromptKey } : {}),
      },
      max_reprompt: MAX_REPROMPT_FIXED,
      fallback_node_id: fallbackNodeId || undefined,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ivrNodes', treeId] });
      onSaved();
    },
    onError: (e) => {
      setServerError(e instanceof Error ? e.message : 'Failed to create node.');
    },
  });

  const updateNode = useMutation({
    mutationFn: () => ivrNodes.update(nodeId!, {
      // Only send node_type if it actually changed — backend resets config +
      // drops edges on any type change, so we don't want to trigger that
      // behavior on a no-op save.
      ...(existingNode && nodeType !== existingNode.node_type ? { node_type: nodeType } : {}),
      name: name.trim(),
      auth_mode: authMode,
      active,
      notes: notes.trim() || undefined,
      // Recording slot fields: when the picker is cleared it sets state
      // to `null`. PATCH must send `null` (not `undefined`) so the
      // backend treats it as "clear this slot". Previously we sent
      // `intro1RecId || undefined` which collapsed `null` → `undefined`
      // → field stripped from the body → backend left the existing
      // value in place. Sam (2026-05-19): "i removed it and pressed
      // save and it just comes back". Same fix for main/outro.
      main_recording_id: mainRecId || null,
      intro1_recording_id: intro1RecId || null,
      intro1_play_mode: intro1PlayMode,
      intro1_skippable: intro1Skippable,
      intro2_recording_id: intro2RecId || null,
      intro2_play_mode: intro2PlayMode,
      intro2_skippable: intro2Skippable,
      outro_recording_id: outroRecId || null,
      outro_skippable: outroSkippable,
      config: {
        ...config,
        ...(mainPromptKey ? { main_prompt_key: mainPromptKey } : {}),
        ...(intro1PromptKey ? { intro1_prompt_key: intro1PromptKey } : {}),
        ...(intro2PromptKey ? { intro2_prompt_key: intro2PromptKey } : {}),
        ...(outroPromptKey ? { outro_prompt_key: outroPromptKey } : {}),
      },
      max_reprompt: MAX_REPROMPT_FIXED,
      fallback_node_id: fallbackNodeId || undefined,
      // When-complete policy (0034/0035). Menus ignore this in practice
      // — the interpreter only consults on_complete for nodes that
      // finish without an outgoing edge — but we still round-trip the
      // current values so a type-switch doesn't silently drop config.
      on_complete: onComplete,
      on_complete_node_id: onComplete === 'custom_node' ? onCompleteNodeId : null,
      auth_list_ids: authListIds,
      // Migration 0076 + 0078 — universal time-frame gate. Applies to
      // any node type; interpreter runs checkTimeFrameGate before the
      // handler. Null fields detach / clear. When the "Restrict by time
      // frame" switch is off, all four columns are nulled on save; when
      // on, the payload is identical to the pre-switch behavior.
      time_frame_id: timeFrameGateOn ? timeFrameId : null,
      time_frame_action: timeFrameGateOn ? timeFrameAction : null,
      time_frame_target_node_id: timeFrameGateOn && timeFrameAction === 'route_to_node' ? timeFrameTargetNodeId : null,
      time_frame_recording_id: timeFrameGateOn ? timeFrameRecordingId : null,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ivrNodes', treeId] });
      qc.invalidateQueries({ queryKey: ['ivrNode', nodeId] });
      // Audit fix (2026-05-15): node-type changes drop edges server-side
      // ("backend resets config + drops edges on any type change"), and
      // updates that affect routing (auth list assignments, time-frame
      // attachments, etc.) can also alter the edge surface. Invalidate
      // the edges-for-tree query so the canvas / list view repaints
      // instead of showing stale arrows.
      qc.invalidateQueries({ queryKey: ['ivrEdgesForTree', treeId] });
      onSaved();
    },
    onError: (e) => {
      setServerError(e instanceof Error ? e.message : 'Failed to update node.');
    },
  });

  const deleteNode = useMutation({
    mutationFn: () => ivrNodes.remove(nodeId!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ivrNodes', treeId] });
      // Deleting a node cascades to its edges in the DB — invalidate the
      // edges query too so they vanish from the canvas immediately.
      qc.invalidateQueries({ queryKey: ['ivrEdgesForTree', treeId] });
      onDeleted?.();
    },
    onError: (e) => {
      const msg = e instanceof Error ? e.message : 'Delete failed.';
      if (msg.includes('node_is_tree_root') || msg.toLowerCase().includes('root')) {
        setServerError('This node is the tree\'s root. Pick a different root node first, then retry.');
      } else {
        setServerError(msg);
      }
      setShowDeleteConfirm(false);
    },
  });

  // ── Validation ──────────────────────────────────────────────────────────────

  function validate(): boolean {
    const errs: Record<string, string> = {};
    if (!name.trim()) errs.name = 'Name is required.';
    if (nodeType === 'transfer') {
      const dest = (config.destination_e164 as string) ?? '';
      if (!dest.trim()) {
        errs.e164 = 'Destination is required.';
      } else if (!/^\+[1-9]\d{1,14}$/.test(dest.trim())) {
        errs.e164 = 'Must be a valid E.164 number (e.g. +15551234567).';
      }
    }
    if (nodeType === 'jump') {
      if (!(config.target_node_id as string)) errs.jump = 'Target node is required.';
    }
    // Delegate validation for all other typed forms to the per-type validator.
    // Errors are prefixed in display-friendly keys for in-form rendering.
    const typedErrs = validateConfigForType(nodeType, config);
    for (const [k, v] of Object.entries(typedErrs)) {
      errs[k] = v;
    }
    // When-complete: custom_node requires a target. Skip for menu-like
    // nodes — the section is hidden for them and their on_complete is
    // never consulted by the interpreter on a successful DTMF branch.
    if (!isMenuLike(nodeType) && onComplete === 'custom_node' && !onCompleteNodeId) {
      errs.on_complete = 'Pick the node to hunt to, or choose a different "when complete" option.';
    }
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  }

  function handleSubmit(e: React.FormEvent) {
    // Defense-in-depth: React synthetic events bubble up the *React* tree,
    // so a form submit inside a Radix Dialog portal (e.g. UploadInlineDialog
    // inside a RecordingPicker inside this drawer) reaches this handler
    // even though the DOM says otherwise. The inner dialog should already
    // have called e.stopPropagation(), but if it didn't, bail rather than
    // clobbering the node with half-stale state. `defaultPrevented` is set
    // when the inner form's handleSubmit called preventDefault, which every
    // recording dialog does.
    if (e.defaultPrevented) return;
    e.preventDefault();
    if (!validate()) return;
    setServerError('');
    if (mode === 'create') {
      createNode.mutate();
    } else {
      updateNode.mutate();
    }
  }

  const isPending = createNode.isPending || updateNode.isPending;
  const otherNodes = treeNodes.filter((n) => n.id !== nodeId);

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40 bg-black/20 backdrop-blur-[1px]"
        onClick={onClose}
        aria-hidden
      />

      {/* Drawer panel */}
      <aside
        role="dialog"
        aria-label={mode === 'create' ? 'Create node' : 'Edit node'}
        aria-modal="true"
        className="fixed right-0 top-0 z-50 h-full w-full max-w-[600px] bg-white shadow-2xl flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4 shrink-0">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">
              {mode === 'create' ? 'Add node' : 'Edit node'}
            </p>
            <h2 className="mt-0.5 text-base font-semibold text-slate-900">
              {mode === 'edit' && existingNode
                ? <><span className="font-mono text-slate-400 mr-2">{formatRef(existingNode.ref_number)}</span>{existingNode.name}</>
                : 'New node'}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition-colors"
            aria-label="Close drawer"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Loading state (edit mode) */}
        {mode === 'edit' && loadingNode ? (
          <div className="flex-1 flex items-center justify-center text-sm text-slate-400">
            Loading node…
          </div>
        ) : (
          <form
            onSubmit={handleSubmit}
            className="flex-1 overflow-y-auto"
            noValidate
          >
            <div className="space-y-6 px-5 py-5">
              {/* ── Core fields ─────────────────────────────────────────────── */}
              <div>
                <SectionHeading>Core</SectionHeading>
                <div className="space-y-3">
                  <Field label="Name" htmlFor="node-name" required error={fieldErrors.name}>
                    <Input
                      id="node-name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="e.g. Main Menu"
                      className="h-9"
                      aria-label="Node name"
                    />
                  </Field>

                  {/* Node type — editable in both create and edit modes.
                      Changing the type in edit mode resets config and deletes
                      existing outgoing edges (branch_keys are type-specific). */}
                  <Field label="Node type" htmlFor="node-type">
                    <Select
                      value={nodeType}
                      onValueChange={(v) => {
                        const newType = v as IvrNodeType;
                        if (mode === 'edit' && newType !== nodeType) {
                          if (!window.confirm(
                            `Change node type from "${nodeType}" to "${newType}"?\n\n` +
                            `This resets the type-specific config to empty and ` +
                            `deletes any outgoing edges (their branch keys may be ` +
                            `invalid for the new type).`,
                          )) {
                            return;
                          }
                        }
                        setNodeType(newType);
                        setConfig({});
                      }}
                    >
                      <SelectTrigger id="node-type" className="h-9">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectGroup>
                          <SelectLabel className="flex items-center gap-1.5 text-[10px]">
                            <Zap className="h-3 w-3 text-amber-500" />
                            Supported (MVP)
                          </SelectLabel>
                          {SUPPORTED_TYPES.map((t) => (
                            <SelectItem key={t} value={t}>
                              <span className="flex items-center gap-2">
                                <span className="font-mono text-xs">{t}</span>
                                <span className="text-[9px] font-semibold text-amber-600 bg-amber-50 border border-amber-200 px-1 rounded">MVP</span>
                              </span>
                            </SelectItem>
                          ))}
                        </SelectGroup>
                        <SelectGroup>
                          <SelectLabel className="flex items-center gap-1.5 text-[10px]">
                            <Clock className="h-3 w-3 text-slate-400" />
                            Coming soon
                          </SelectLabel>
                          {UNSUPPORTED_TYPES.map((t) => (
                            <SelectItem key={t} value={t}>
                              <span className="font-mono text-xs text-slate-500">{t}</span>
                            </SelectItem>
                          ))}
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                  </Field>

                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Auth mode" htmlFor="node-auth-mode">
                      <Select value={authMode} onValueChange={(v) => setAuthMode(v as AuthMode)}>
                        <SelectTrigger id="node-auth-mode" className="h-9 text-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {AUTH_MODES.map((m) => (
                            <SelectItem key={m} value={m} className="text-sm">
                              {AUTH_LABELS[m]}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Field>

                    {mode === 'edit' && (
                      <div className="flex flex-col gap-1">
                        <Label className="text-xs font-medium text-slate-600">Active</Label>
                        <div className="flex h-9 items-center">
                          <Switch
                            checked={active}
                            onCheckedChange={setActive}
                            aria-label="Node active"
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Max reprompts field removed (Sam 2026-05-19). The
                      engine pins menu nodes to 3 retries (MENU_MAX_ATTEMPTS)
                      and ignores the per-node column entirely — exposing
                      a slider was misleading. Backend column stays for
                      back-compat; service writes 3 on create. */}

                </div>
              </div>

              {/* ── Recordings ──────────────────────────────────────────────── */}
              <div>
                <SectionHeading>Recordings</SectionHeading>
                <div className="space-y-3">
                  <RecordingPicker label="Main recording" value={mainRecId} onChange={setMainRecId} />

                  <div className="space-y-2">
                    <RecordingPicker label="Intro 1" value={intro1RecId} onChange={setIntro1RecId} />
                    {intro1RecId && (
                      <div className="space-y-1.5 ms-1">
                        <label className="flex items-center gap-2 text-xs text-slate-600">
                          <input
                            type="checkbox"
                            checked={intro1PlayMode !== 'off'}
                            onChange={(e) => setIntro1PlayMode(e.target.checked ? 'once' : 'off')}
                          />
                          Play this intro when the caller reaches the node
                          {intro1PlayMode === 'off' && (
                            <span className="text-slate-400">(off — won’t play)</span>
                          )}
                        </label>
                        <label className="flex items-center gap-2 text-xs text-slate-600">
                          <input
                            type="checkbox"
                            checked={intro1Skippable}
                            onChange={(e) => setIntro1Skippable(e.target.checked)}
                          />
                          Skippable — caller can press a key to interrupt
                          {!intro1Skippable && (
                            <span className="text-amber-700 font-medium">(DTMF disabled while playing)</span>
                          )}
                        </label>
                      </div>
                    )}
                  </div>

                  <div className="space-y-2">
                    <RecordingPicker label="Intro 2" value={intro2RecId} onChange={setIntro2RecId} />
                    {intro2RecId && (
                      <div className="space-y-1.5 ms-1">
                        <label className="flex items-center gap-2 text-xs text-slate-600">
                          <input
                            type="checkbox"
                            checked={intro2PlayMode !== 'off'}
                            onChange={(e) => setIntro2PlayMode(e.target.checked ? 'once' : 'off')}
                          />
                          Play this intro when the caller reaches the node
                          {intro2PlayMode === 'off' && (
                            <span className="text-slate-400">(off — won’t play)</span>
                          )}
                        </label>
                        <label className="flex items-center gap-2 text-xs text-slate-600">
                          <input
                            type="checkbox"
                            checked={intro2Skippable}
                            onChange={(e) => setIntro2Skippable(e.target.checked)}
                          />
                          Skippable — caller can press a key to interrupt
                          {!intro2Skippable && (
                            <span className="text-amber-700 font-medium">(DTMF disabled while playing)</span>
                          )}
                        </label>
                      </div>
                    )}
                  </div>

                  <div className="space-y-2">
                    <RecordingPicker label="Outro" value={outroRecId} onChange={setOutroRecId} />
                    <p className="text-[11px] text-slate-400 ms-1">{outroBehaviorText(nodeType)}</p>
                    {outroRecId && (
                      <label className="flex items-center gap-2 text-xs text-slate-600 ms-1">
                        <input
                          type="checkbox"
                          checked={outroSkippable}
                          onChange={(e) => setOutroSkippable(e.target.checked)}
                        />
                        Skippable — caller can press a key to interrupt
                        {!outroSkippable && (
                          <span className="text-amber-700 font-medium">(DTMF disabled while playing)</span>
                        )}
                      </label>
                    )}
                  </div>
                </div>
              </div>

              {/* ── Per-type config ──────────────────────────────────────────
                  Deliberately AFTER Recordings (Sam 2026-08-06): the drawer
                  reads identity → recordings → type config → the rest. */}
              <div>
                <SectionHeading>Type config</SectionHeading>
                <div className="space-y-3">
                  {nodeType === 'playback' && <PlaybackConfig />}
                  {nodeType === 'menu' && (
                    <MenuConfig
                      config={config}
                      onChange={setConfig}
                    />
                  )}
                  {nodeType === 'transfer' && (
                    <TransferConfig
                      config={config}
                      onChange={setConfig}
                      error={fieldErrors.e164}
                    />
                  )}
                  {nodeType === 'jump' && (
                    <>
                      <JumpConfig
                        config={config}
                        onChange={setConfig}
                        treeNodes={treeNodes}
                        currentNodeId={nodeId}
                      />
                      {fieldErrors.jump && (
                        <p role="alert" className="text-xs text-red-500 mt-1">{fieldErrors.jump}</p>
                      )}
                    </>
                  )}
                  {!['playback', 'menu', 'transfer', 'jump'].includes(nodeType) && (
                    TYPED_NODE_TYPES.has(nodeType) ? (
                      <TypedConfigForm
                        nodeType={nodeType}
                        config={config}
                        onChange={setConfig}
                        nodeId={nodeId ?? undefined}
                        nodeRefNumber={existingNode?.ref_number != null ? Number(existingNode.ref_number) : null}
                        treeNodes={treeNodes}
                      />
                    ) : (
                      <GenericConfig config={config} onChange={setConfig} />
                    )
                  )}

                  {/* Universal time-frame gate (0076 + 0078). Any node type
                      can use one; when attached, the interpreter
                      short-circuits the node outside the frame's schedule —
                      optionally playing a recording first, then doing the
                      configured action. The whole gate sits behind the
                      "Restrict by time frame" switch: off hides the fields,
                      and saving while off detaches the frame. */}
                  <div className="rounded-md border border-slate-200 bg-slate-50/60 px-3 py-2.5 space-y-3">
                    <div className="flex items-start gap-3">
                      <Switch
                        id="node-tf-gate"
                        checked={timeFrameGateOn}
                        onCheckedChange={setTimeFrameGateOn}
                        className="mt-0.5 shrink-0"
                      />
                      <div>
                        <Label htmlFor="node-tf-gate" className="text-xs font-medium text-slate-700 cursor-pointer">
                          Restrict by time frame
                        </Label>
                        <p className="text-[11px] text-slate-400 mt-0.5 leading-snug">
                          When on, this node only runs inside the chosen time
                          frame&apos;s schedule — outside it, the off-hours
                          behavior runs instead.
                        </p>
                        {!timeFrameGateOn && timeFrameId && (
                          <p className="text-[11px] text-amber-600 mt-1">
                            A time frame is attached — saving will remove it.
                          </p>
                        )}
                      </div>
                    </div>
                    {timeFrameGateOn && (
                      <div className="border-t border-slate-200 pt-3">
                        <TimeFramePicker
                          value={{
                            time_frame_id: timeFrameId,
                            time_frame_action: timeFrameAction,
                            time_frame_target_node_id: timeFrameTargetNodeId,
                            time_frame_recording_id: timeFrameRecordingId,
                          }}
                          onChange={(next) => {
                            setTimeFrameId(next.time_frame_id);
                            setTimeFrameAction(next.time_frame_action);
                            setTimeFrameTargetNodeId(next.time_frame_target_node_id);
                            setTimeFrameRecordingId(next.time_frame_recording_id);
                          }}
                          treeNodes={treeNodes}
                          currentNodeId={nodeId ?? undefined}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* ── Fallback node ────────────────────────────────────────────── */}
              <div>
                <SectionHeading>Fallback</SectionHeading>
                <Field label="Fallback node" htmlFor="node-fallback" hint="Where to route if this node fails. None = hang up.">
                  <Select
                    value={fallbackNodeId ?? '__none__'}
                    onValueChange={(v) => setFallbackNodeId(v === '__none__' ? null : v)}
                  >
                    <SelectTrigger id="node-fallback" className="h-9 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">None (hang up on failure)</SelectItem>
                      {otherNodes.map((n) => (
                        <SelectItem key={n.id} value={n.id}>
                          <span className="flex items-center gap-1.5">
                            <span className="font-mono text-[11px] text-slate-400">{formatRef(n.ref_number)}</span>
                            {n.name}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
              </div>

              {/* ── When complete (non-menu nodes only) ─────────────────────────
                  Menus branch via DTMF edges, so their on_complete policy
                  is rarely meaningful — hide the section for them so tree
                  authors don't set a value that will silently be ignored. */}
              {!isMenuLike(nodeType) && (
                <div>
                  <SectionHeading>When complete</SectionHeading>
                  <WhenCompleteSection
                    mode={onComplete}
                    targetId={onCompleteNodeId}
                    treeNodes={otherNodes}
                    error={fieldErrors.on_complete}
                    onModeChange={(m) => {
                      setOnComplete(m);
                      // Clear the target when switching away from
                      // custom_node so a stale id doesn't silently ride
                      // along with a 'hunt_back' / 'hangup' save.
                      if (m !== 'custom_node') setOnCompleteNodeId(null);
                    }}
                    onTargetChange={setOnCompleteNodeId}
                  />
                </div>
              )}

              {/* ── Notes ───────────────────────────────────────────────────── */}
              <div>
                <SectionHeading>Notes</SectionHeading>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  maxLength={2000}
                  rows={3}
                  placeholder="Optional internal notes about this node…"
                  className="text-sm resize-none"
                  aria-label="Node notes"
                />
                <p className="mt-1 text-right text-[10px] text-slate-300">{notes.length}/2000</p>
              </div>

              {/* ── Parent node (edit mode only) ─────────────────────────────── */}
              {mode === 'edit' && nodeId && (
                <ParentNodeSection
                  nodeId={nodeId}
                  treeId={treeId}
                  treeNodes={treeNodes}
                  canMutate={canMutate}
                />
              )}

              {/* ── Edges (edit mode only) ───────────────────────────────────── */}
              {mode === 'edit' && nodeId && (
                <div>
                  <SectionHeading>Edges</SectionHeading>
                  {nodeType === 'jump' ? (
                    <p className="text-xs text-slate-400 italic">
                      Jump nodes follow their target directly — edges are not used.
                    </p>
                  ) : (
                    <EdgesList
                      fromNodeId={nodeId}
                      treeNodes={treeNodes}
                      canMutate={canMutate}
                    />
                  )}
                </div>
              )}

              {/* ── Per-node system prompt overrides (super-admin, edit mode) ─
                  Node-level overrides beat customer / reseller / global in the
                  resolver chain. Only surfaced to super-admin — backend also
                  enforces this at the API layer. */}
              {mode === 'edit' && nodeId && isSuperAdmin && (
                <div>
                  <SectionHeading>System prompt overrides</SectionHeading>
                  <NodePromptOverridesPanel nodeId={nodeId} nodeType={nodeType} />
                </div>
              )}

              {/* ── Delete (edit mode only) ──────────────────────────────────── */}
              {mode === 'edit' && canMutate && (
                <div>
                  <SectionHeading>Danger zone</SectionHeading>
                  {showDeleteConfirm ? (
                    <DeleteConfirm
                      onConfirm={() => deleteNode.mutate()}
                      onCancel={() => setShowDeleteConfirm(false)}
                      isPending={deleteNode.isPending}
                    />
                  ) : (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="border-red-200 text-red-600 hover:bg-red-50 hover:border-red-300"
                      onClick={() => setShowDeleteConfirm(true)}
                    >
                      <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                      Delete node
                    </Button>
                  )}
                </div>
              )}

              {/* Server error */}
              {serverError && (
                <p role="alert" className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                  {serverError}
                </p>
              )}
            </div>

            {/* Footer */}
            {canMutate && (
              <div className="sticky bottom-0 border-t border-slate-100 bg-white px-5 py-3 flex items-center justify-end gap-2 shrink-0">
                <Button type="button" variant="outline" size="sm" onClick={onClose} disabled={isPending}>
                  Cancel
                </Button>
                <Button type="submit" size="sm" disabled={isPending}>
                  {isPending
                    ? (mode === 'create' ? 'Creating…' : 'Saving…')
                    : (mode === 'create' ? 'Create node' : 'Save changes')}
                </Button>
              </div>
            )}
          </form>
        )}
      </aside>
    </>
  );
}
